import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const StockTransferContext = createContext();

export const StockTransferProvider = ({ children }) => {
  const [stockTransfers, setStockTransfers] = useState({
    data: [],
    meta: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
    filters: {
      status: "all", // "all", "completed", "pending", etc.
      from_warehouse: "",
      to_warehouse: "",
      date_from: "",
      date_to: "",
    },
  });



  const queryString = useMemo(() => {
    const { status, from_warehouse, to_warehouse, date_from, date_to } = filterOptions.filters;
    const queryParams = {
      search: filterOptions.search || "",
      page: filterOptions.currentPage,
      paginate: 10,
      ...(status && status !== "all" && { [`filter[status]`]: status }),
      ...(from_warehouse && { [`filter[from_warehouse_id]`]: from_warehouse }),
      ...(to_warehouse && { [`filter[to_warehouse_id]`]: to_warehouse }),
      ...(date_from && { from_date: date_from }),
      ...(date_to && { to_date: date_to }),
      include: "from_warehouse,to_warehouse,responsible_staff,creator,items.inventory",
    };

    return buildQueryString(queryParams);
  }, [filterOptions]);

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
        currentPage: 1, // Reset to first page when searching
      }));
    }
  };

  // 🔹 Optimasi updateFilter agar tidak menyebabkan re-render yang tidak perlu
  const updateFilter = useCallback((key, value) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      filters: {
        ...prevState.filters,
        [key]: value,
      },
      currentPage: 1, // Reset to first page when filtering
    }));
  }, []);

  const handleStatusFilter = (status) => {
    updateFilter("status", status);
  };

  const handleFromWarehouseFilter = (warehouseId) => {
    updateFilter("from_warehouse", warehouseId);
  };

  const handleToWarehouseFilter = (warehouseId) => {
    updateFilter("to_warehouse", warehouseId);
  };

  const handleDateFilter = (dateFrom, dateTo) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      filters: {
        ...prevState.filters,
        date_from: dateFrom,
        date_to: dateTo,
      },
      currentPage: 1, // Reset to first page when filtering
    }));
  };

  const handleDateFromFilter = (dateFrom) => {
    updateFilter("date_from", dateFrom);
  };

  const handleDateToFilter = (dateTo) => {
    updateFilter("date_to", dateTo);
  };

  const fetchStockTransfers = useCallback(async () => {
    setStockTransfers((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      console.log("Fetching stock transfers with query:", queryString);
      const response = await getRequest(`/stock-transfers${queryString}`);
      console.log("Stock Transfers API Response:", response);

      if (response.success) {
        setStockTransfers({
          data: response.data,
          meta: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination?.last_page || 1,
        }));

        console.log(
          "Stock transfers loaded:",
          response.data?.length || 0,
          "items",
        );
      } else {
        throw new Error(response.message || "Failed to fetch stock transfers");
      }
    } catch (error) {
      console.error("Error fetching stock transfers:", error);
      setStockTransfers((prev) => ({
        ...prev,
        error: error.message || "Failed to fetch stock transfers",
        loading: false,
      }));
    }
  }, [queryString]);

  useEffect(() => {
    fetchStockTransfers();
  }, [fetchStockTransfers]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = stockTransfers.meta?.last_page || 1;
  console.log(
    "Total pages calculated:",
    totalPages,
    "from meta:",
    stockTransfers.meta,
  );

  const Pagination = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  return (
    <StockTransferContext.Provider
      value={{
        stockTransfers,
        filterOptions,
        updateFilter, // 🔥 Add updateFilter method like BookingProviderV3
        handleSearch,
        handleStatusFilter,
        handleFromWarehouseFilter,
        handleToWarehouseFilter,
        handleDateFilter,
        handleDateFromFilter,
        handleDateToFilter,
        refetch: fetchStockTransfers,
        Pagination,
      }}
    >
      {children}
    </StockTransferContext.Provider>
  );
};

export const useStockTransfer = () => {
  const context = useContext(StockTransferContext);
  if (!context) {
    throw new Error(
      "useStockTransfer must be used within StockTransferProvider",
    );
  }
  return context;
};
